import cookies from "js-cookie";

import { get, getItem, http, IHttpRequestConfig, IHttpResponse, post, setItem } from "@/browser";
import { applyHeaders, extractDataFromResponse } from "@/core/api";
import { hotjarIdentify } from "@/core/tracking/hotjar";
import { compose, getQueryVariable } from "@/core/url";

import {
  ICredentials,
  IEmailConfirmation,
  IEmailConfirmationResponse,
  IPasswordReset,
  IPasswordResetStatus,
  IPasswordSetup,
  IPasswordSetupResponse,
  IRegisterUser,
  IResendEmailConfirmation,
  ISessionUser,
  ISocialLogin,
  ISocialRegistration,
  sessionUserType,
  TOAuthActionType,
  TOAuthServiceType,
} from "./types";

export const API = {
  login: "/api/auth/login",
  logout: "/api/auth/logout",
  refresh: "/api/auth/refresh",
  xAuthToken: "/api/auth/token",
  registerUser: "/api/public/users/registration",
  passwordReset: "/api/public/users/password/reset",
  passwordSetup: "/api/public/users/password/setup",
  passwordStatus: "/api/public/users/password/status",
  emailConfirmation: "/api/public/users/email-confirmation",
  resendEmailConfirmation: "/api/public/users/resend-email-confirmation",
  socialRegistration: "/api/public/auth-social/registration",
  socialLogin: "/api/public/auth-social/login",
  linkedInOauthPath: "/oauth2/authorization/linkedin",
  googleOauthPath: "/oauth2/authorization/google",
  unsubscribe: "/api/public/consultant-profiles/unsubscribe",
  loginOnBehalf: "/api/public/login-on-behalf",
};

interface IRecaptchaParam {
  recaptchaToken?: string;
}

interface ILoginParams extends IRecaptchaParam {}

export const login = ({ recaptchaToken }: ILoginParams, data: ICredentials, options?: IHttpRequestConfig) => {
  setAuthToken(null);
  const payload = new FormData();
  payload.append("username", data.username.toLowerCase());
  payload.append("password", data.password);
  return post<Partial<ISessionUser>>(
    API.login,
    payload,
    applyHeaders({ hubspotutk: cookies.get("hubspotutk"), recaptchaToken }, options),
  )
    .then(extractDataFromResponse)
    .then(mapSessionUserType)
    .then(sessionUserType);
};

const mapSessionUserType = (data: { emails: any[] }) => {
  return {
    ...data,
    email: data.emails.find(e => e.primary).email,
  };
};

export const logout = () => {
  sessionStorage.clear();
  hotjarIdentify();
  return post<undefined>(API.logout)
    .then(extractDataFromResponse)
    .then(() => setAuthToken(null))
    .then(clearChatAuthToken);
};

export const refresh = () => {
  const consultantProfileId = getConsultantProfileId();

  return get<Partial<ISessionUser>>(API.refresh, applyHeaders({ consultantProfileId }))
    .then(extractDataFromResponse)
    .then(mapSessionUserType)
    .then(sessionUserType);
};

export interface ILoginOnBehalfDto {
  token: string;
}

export const loginOnBehalf = (dto: ILoginOnBehalfDto, options?: IHttpRequestConfig) => {
  return post<Partial<ISessionUser>>(API.loginOnBehalf, dto, options)
    .then(extractDataFromResponse)
    .then(mapSessionUserType)
    .then(sessionUserType);
};

interface IXAuthTokenParams {
  companyId: number;
}

export const xAuthToken = (params: IXAuthTokenParams, options: IHttpRequestConfig = {}) => {
  return get<string>(API.xAuthToken, applyHeaders(params, options)).then(extractDataFromResponse);
};

interface IRegisterUserParams extends IRecaptchaParam {}

export const registerUser = (
  { recaptchaToken }: IRegisterUserParams,
  data: IRegisterUser,
  options?: IHttpRequestConfig,
) => {
  setAuthToken(null);
  return post<Partial<ISessionUser>>(
    API.registerUser,
    {
      ...data,
      language: data.language.toUpperCase(),
    },
    applyHeaders({ hubspotutk: cookies.get("hubspotutk"), recaptchaToken }, options),
  )
    .then(extractDataFromResponse)
    .then(mapSessionUserType)
    .then(sessionUserType);
};

interface IPasswordResetParams extends IRecaptchaParam {}

export const passwordReset = (
  { recaptchaToken }: IPasswordResetParams,
  data: IPasswordReset,
  options?: IHttpRequestConfig,
) => {
  return post<true>(API.passwordReset, data, applyHeaders({ recaptchaToken }, options)).then(extractDataFromResponse);
};

interface IPasswordSetupParams {}

export const passwordSetup = (_params: IPasswordSetupParams, data: IPasswordSetup, options?: IHttpRequestConfig) => {
  return post<IPasswordSetupResponse>(API.passwordSetup, data, options).then(extractDataFromResponse);
};

export const fetchPasswordStatus = (token: string) => {
  const url = compose(API.passwordStatus, {}, { token });
  return get<IPasswordResetStatus>(url).then(extractDataFromResponse);
};

interface IEmailConfirmationParams {}

export const emailConfirmation = (
  _params: IEmailConfirmationParams,
  data: IEmailConfirmation,
  options?: IHttpRequestConfig,
) => {
  return post<IEmailConfirmationResponse>(API.emailConfirmation, data, options).then(extractDataFromResponse);
};

interface IResendEmailConfirmationParams {}

export const resendEmailConfirmation = (
  _params: IResendEmailConfirmationParams,
  data: IResendEmailConfirmation,
  options?: IHttpRequestConfig,
) => {
  return post<true>(API.resendEmailConfirmation, data, options).then(extractDataFromResponse);
};

interface IUnsubscribeParams {}

interface IUnsubscribeData {
  token: string;
}

export const unsubscribe = (_params: IUnsubscribeParams, data: IUnsubscribeData, options?: IHttpRequestConfig) => {
  return post<true>(API.unsubscribe, data, options).then(extractDataFromResponse);
};

export const STORAGE_KEY_TOKEN = "authorization";
export const CHAT_STORAGE_KEY_TOKEN = "chat-api-authorization";
export const LOGIN_ON_BEHALF_KEY_TOKEN = "loginOnBehalf";
export const CONSULTANT_PROFILE_ID_SESSION_STORAGE_KEY = "consultantProfileId";

export const getLoginOnBehalfToken = (): string | undefined => {
  return getQueryVariable(LOGIN_ON_BEHALF_KEY_TOKEN);
};

export const setConsultantProfileId = (consultantProfileId?: number) => {
  return setItem(CONSULTANT_PROFILE_ID_SESSION_STORAGE_KEY, consultantProfileId);
};

export const getConsultantProfileId = () => {
  return getItem<number>(CONSULTANT_PROFILE_ID_SESSION_STORAGE_KEY);
};

export const getAuthToken = (): string | undefined => {
  return getItem<string>(STORAGE_KEY_TOKEN);
};

export const setAuthToken = (token: string | null) => {
  return setItem(STORAGE_KEY_TOKEN, token);
};

export const updateAuthToken = (token: string | undefined) => {
  if (token) {
    const prevToken = getAuthToken();
    if (token !== prevToken) {
      setAuthToken(token);
    }
  }
};

export const getChatAuthToken = (): string | undefined => {
  return getItem<string>(CHAT_STORAGE_KEY_TOKEN);
};

export const updateChatAuthToken = (token: string | undefined) => {
  if (token) {
    const prevToken = getChatAuthToken();
    if (token !== prevToken) {
      setItem(CHAT_STORAGE_KEY_TOKEN, token);
    }
  }
};

export const clearChatAuthToken = () => {
  setItem(CHAT_STORAGE_KEY_TOKEN, "");
};

export const onRequest = (request: IHttpRequestConfig) => {
  const token = getAuthToken();
  if (token) {
    request.headers[STORAGE_KEY_TOKEN] = token;
  }
};

export const onSuccessResponse = (response: IHttpResponse) => {
  if (response.headers) {
    updateAuthToken(response.headers[STORAGE_KEY_TOKEN]);
    if (response.config.url === API.refresh) {
      updateChatAuthToken(response.headers[STORAGE_KEY_TOKEN]);
    }
  }
};

export const socialRegistration = (data: ISocialRegistration, options?: IHttpRequestConfig) => {
  return post<true>(
    API.socialRegistration,
    {
      ...data,
      language: data.language.toUpperCase(),
    },
    applyHeaders({ hubspotutk: cookies.get("hubspotutk") }, options),
  ).then(extractDataFromResponse);
};

export const socialLogin = (data: ISocialLogin, options?: IHttpRequestConfig) => {
  return post<true>(API.socialLogin, data, applyHeaders({ hubspotutk: cookies.get("hubspotutk") }, options)).then(
    extractDataFromResponse,
  );
};

export const getOAuthURL = (
  serviceType: TOAuthServiceType,
  actionType: TOAuthActionType,
  redirectBaseUri: string,
  redirectQueryString?: string,
) => {
  const baseURL = http.defaults.baseURL;
  const path = serviceType === "LinkedIn" ? API.linkedInOauthPath : API.googleOauthPath;

  const redirectUriQueryString = redirectQueryString ? `?${redirectQueryString}` : "";
  const redirectUri = encodeURIComponent(`${redirectBaseUri}/social/${actionType}${redirectUriQueryString}`);

  const registrationRedirectUri = encodeURIComponent(`${redirectBaseUri}/agreement/${actionType}`);
  const queryParams = `redirect_uri=${redirectUri}&registration_redirect_uri=${registrationRedirectUri}`;

  return `${baseURL}${path}?${queryParams}`;
};
